'use client';

import { useEffect } from 'react';
import { useStore } from '@/store/store';

/**
 * Resource Monitor Component (Development Only)
 * TODO - check how we can remove lazy loaded images
 * Monitors resource loading performance and logs issues to console:
 * - Slow resources (>1s)
 * - High queueing time (>100ms)
 *
 * Controlled by debug menu toggle: debug.resourceMonitorEnabled
 *
 * Note: In production, Datadog RUM automatically tracks all resource timing.
 * This component is for local development debugging only.
 */
export const ResourceMonitor = () => {
    const debug = useStore((s) => s.debug);

    useEffect(() => {
        if (process.env.NODE_ENV === 'production' || !debug.resourceMonitorEnabled) {
            return () => {};
        }

        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();

                entries.forEach((entry) => {
                    if (entry.entryType === 'resource') {
                        const resourceEntry = entry as PerformanceResourceTiming;

                        // Calculate timing metrics
                        const queueTime = resourceEntry.requestStart - resourceEntry.fetchStart;
                        const downloadTime = resourceEntry.responseEnd - resourceEntry.responseStart;
                        const totalDuration = resourceEntry.duration;

                        const isImage =
                            resourceEntry.initiatorType === 'img' ||
                            resourceEntry.name.match(/\.(jpg|jpeg|png|gif|webp|svg|avif)(\?|$)/i);

                        const fileName = resourceEntry.name.split('/').pop() || resourceEntry.name;

                        // Log resources with high queueing time (> 100ms)
                        if (queueTime > 100) {
                            console.warn(
                                '%c[Resource Monitor] 🕐 High Queueing Time',
                                'color: #ff9800; font-weight: bold;',
                                '\n',
                                isImage ? '🖼️' : '📄',
                                fileName,
                                '\n',
                                {
                                    queueTime: `${Math.round(queueTime)}ms`,
                                    downloadTime: `${Math.round(downloadTime)}ms`,
                                    totalDuration: `${Math.round(totalDuration)}ms`,
                                    type: resourceEntry.initiatorType,
                                    url: resourceEntry.name
                                }
                            );
                        }

                        // Log slow resources (> 1 second total)
                        if (totalDuration > 1000) {
                            console.warn(
                                '%c[Resource Monitor] ⚠️ Slow Resource',
                                'color: #ff4e42; font-weight: bold;',
                                '\n',
                                isImage ? '🖼️' : '📄',
                                fileName,
                                '\n',
                                {
                                    totalDuration: `${Math.round(totalDuration)}ms`,
                                    queueTime: `${Math.round(queueTime)}ms`,
                                    downloadTime: `${Math.round(downloadTime)}ms`,
                                    type: resourceEntry.initiatorType,
                                    url: resourceEntry.name
                                }
                            );
                        }
                    }
                });
            });

            observer.observe({ entryTypes: ['resource'] });

            // Log that monitoring is active
            console.log('%c[Resource Monitor] 👀 Active', 'color: #2196f3; font-weight: bold; font-size: 11px;');
            console.log(
                '%c[Resource Monitor] Watching for slow resources (>1s) and high queueing (>100ms)',
                'color: #666; font-size: 11px;'
            );

            return () => {
                observer.disconnect();
                console.log('%c[Resource Monitor] 🛑 Stopped', 'color: #999; font-size: 11px;');
            };
        } catch (error) {
            console.warn('[Resource Monitor] PerformanceObserver not supported', error);
        }
    }, [debug.resourceMonitorEnabled]);

    return null;
};

/**
 * Helper function to check current resource queueing
 * Available in browser console as window.checkResourceQueueing()
 */
export const setupResourceQueueingHelper = () => {
    if (process.env.NODE_ENV === 'production') {
        return () => {};
    }

    (window as any).checkResourceQueueing = () => {
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

        const queueingIssues = resources
            .map((r) => ({
                name: r.name.split('/').pop() || r.name,
                queueTime: Math.round(r.requestStart - r.fetchStart),
                downloadTime: Math.round(r.responseEnd - r.responseStart),
                totalTime: Math.round(r.duration),
                type: r.initiatorType,
                isImage: r.initiatorType === 'img' || r.name.match(/\.(jpg|jpeg|png|gif|webp|svg|avif)(\?|$)/i)
            }))
            .filter((r) => r.queueTime > 50)
            .sort((a, b) => b.queueTime - a.queueTime);

        if (queueingIssues.length > 0) {
            console.group(
                `%c[Resource Monitor] 🕐 Resources with Queueing (${queueingIssues.length} total)`,
                'color: #ff9800; font-weight: bold;'
            );
            console.table(queueingIssues);
            console.log(
                '%c💡 Tips to reduce queueing:',
                'color: #2196f3; font-weight: bold;',
                '\n• Use HTTP/2 or HTTP/3 for multiplexing',
                '\n• Lazy load images below the fold',
                '\n• Reduce concurrent requests',
                '\n• Use image sprites or CSS backgrounds',
                '\n• Implement resource prioritization',
                '\n• Use a CDN with better connection limits',
                '\n\n📊 Note: Datadog RUM automatically tracks all resource timing in production'
            );
            console.groupEnd();
        } else {
            console.log(
                '%c[Resource Monitor] ✅ No significant resource queueing detected',
                'color: #4caf50; font-weight: bold;'
            );
        }

        return queueingIssues;
    };

    console.log(
        '%c[Resource Monitor] 🛠️ Helper available: window.checkResourceQueueing()',
        'color: #666; font-size: 11px;'
    );
};
